import type { ITourStep } from '@components/PageTour/types';
import { closeOnLeave } from '@components/PageTour/stepHelpers';

export const DOCUMENTS_TOUR_ID = 'documents';

/** What the script's steps read off the page. */
export interface IDocumentsTourContext {
  /** The tree has at least one folder — the open-a-folder step's real control. */
  hasFolders: boolean;
  /** A document row (or card) is listed — the click-a-document step's real
   *  control. False at the root, in an empty folder, and on a search that
   *  matches nothing. */
  hasDocumentRow: boolean;
  /** The user may upload — the Upload button (and the dialog it opens) is
   *  rendered only then. */
  canUpload: boolean;
  /** Closes the upload dialog the upload step opened. */
  closeUpload: () => void;
  /** Closes the drawer the click-a-document step opened. */
  closeDrawer: () => void;
}

const noFolders = (ctx: IDocumentsTourContext) => !ctx.hasFolders;
const noUpload = (ctx: IDocumentsTourContext) => !ctx.canUpload;
const noDocument = (ctx: IDocumentsTourContext) => !ctx.hasDocumentRow;

/**
 * The Documents tour, left to right. Copy lives in `tours.documents.<stepId>.*`
 * in both locales; targets are `data-tour` attributes on the page. Three
 * action steps drive real controls — a folder in the tree, the Upload button,
 * a document row — and the steps that depend on what they open are skipped
 * when the control is not on the page.
 */
export const DOCUMENTS_TOUR_STEPS: ITourStep<IDocumentsTourContext>[] = [
  { id: 'welcome' },
  { id: 'folders', target: 'documents-folders', side: 'right', align: 'start' },
  {
    id: 'openFolder',
    // The first folder under the root: `documents-folder` is on every folder
    // node, and the engine takes the first match.
    target: 'documents-folder',
    side: 'right',
    pad: 4,
    action: { target: 'documents-folder' },
    skipWhen: noFolders,
  },
  { id: 'search', target: 'documents-search', side: 'bottom', align: 'start' },
  { id: 'views', target: 'documents-views', side: 'bottom' },
  { id: 'rows', target: 'documents-pane', side: 'inside', scroll: 'start' },
  {
    id: 'upload',
    target: 'documents-upload',
    side: 'bottom',
    align: 'end',
    pad: 8,
    action: { target: 'documents-upload' },
    skipWhen: noUpload,
  },
  {
    id: 'uploadWhere',
    target: ['documents-upload-destination', 'documents-upload-file'],
    side: 'left',
    skipWhen: noUpload,
    onLeave: closeOnLeave('back', (ctx) => ctx.closeUpload()),
  },
  {
    id: 'uploadHow',
    target: ['documents-upload-comment', 'documents-upload-approval'],
    side: 'left',
    skipWhen: noUpload,
    onLeave: closeOnLeave('forward', (ctx) => ctx.closeUpload()),
  },
  {
    id: 'openDoc',
    target: 'documents-doc',
    side: 'bottom',
    align: 'start',
    pad: 4,
    action: { target: 'documents-doc' },
    skipWhen: noDocument,
    // A replay from inside a folder can arrive here with the first document
    // already open, and its row click would TOGGLE the drawer closed. Close
    // whatever is open first, so the click always opens.
    onEnter: (ctx) => ctx.closeDrawer(),
  },
  {
    id: 'docDetails',
    target: ['documents-drawer-meta', 'documents-drawer-preview'],
    side: 'left',
    skipWhen: noDocument,
    onLeave: closeOnLeave('back', (ctx) => ctx.closeDrawer()),
  },
  { id: 'docActions', target: 'documents-drawer-actions', side: 'left', skipWhen: noDocument },
  {
    id: 'docVersions',
    target: 'documents-drawer-versions',
    side: 'left',
    skipWhen: noDocument,
    onLeave: closeOnLeave('forward', (ctx) => ctx.closeDrawer()),
  },
  { id: 'done' },
];
